import { AnalyticsPageViews } from "./analytics/AnalyticsPageViews";
import React, { useEffect } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import { ErrorModal } from "./components/atoms/ErrorModal";
import { GlobalLoader } from "./components/atoms/GlobalLoader";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import NotFound from "./components/common/NotFound";
import { PinLockProvider } from "./components/common/PinLockProvider";
import { PrivateRoute } from "./components/common/PrivateRoute";
import { useFacilityUser } from "./hooks/useFacilityUser";
import { SessionProvider } from "./components/common/SessionProvider";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { PasswordChangeRequiredModal } from "./components/molecules/PasswordChangeRequiredModal/PasswordChangeRequiredModal";
import { useHasLicence } from "./hooks/useHasLicence";
import { useScrollToTop } from "./hooks/useScrollToTop";
import { selectFeatureFlag } from "./store/reducers/sessionSlice";
import { ChangePassword } from "./pages/ChangePassword";
import { AppointmentDetails } from "./pages/manager/AppointmentDetails";
import { ManagerDashboard } from "./pages/manager/Dashboard";
import { EmergencyManagement } from "./pages/manager/EmergencyManagement";
import { FacilitySettings } from "./pages/manager/FacilitySettings";
import { HelpSupport } from "./pages/manager/HelpSupport";
import { ManageAppointments } from "./pages/manager/ManageAppointments";
import { ManagePatients } from "./pages/manager/ManagePatients";
import { ManageStaff } from "./pages/manager/ManageStaff";
import { Notifications } from "./pages/manager/Notifications";
import { PatientDetails } from "./pages/manager/PatientDetails";
import { StaffDetails } from "./pages/manager/StaffDetails";
import { UserProfile } from "./pages/manager/UserProfile";
import { UserSettings } from "./pages/manager/UserSettings";
import { ViewReports } from "./pages/manager/ViewReports";
import { LicencePage } from "./pages/manager/LicencePage";
import { PaymentCallbackPage } from "./pages/payment/PaymentCallbackPage";
import { PaymentCancelledPage } from "./pages/payment/PaymentCancelledPage";
import { ManageAvailability } from "./pages/secretary/ManageAvailability";
import { SetPassword } from "./pages/SetPassword";
import { resetLoader } from "./store/reducers/loaderSlice";
import { RootState, store } from "./store/store";

// Dashboard component (shared by manager and secretary)
const DashboardRouter = () => {
  return <ManagerDashboard />;
};

// Component to show password change required modal
const PasswordChangeRequiredWrapper: React.FC = () => {
  const { session } = useSelector((state: RootState) => state.session);
  const location = useLocation();

  // Check if password change is required (don't show on change-password or set-password pages)
  const requiresPasswordChange =
    session?.user?.requiresPasswordChange === true &&
    location.pathname !== "/change-password" &&
    location.pathname !== "/set-password";

  return <PasswordChangeRequiredModal isOpen={requiresPasswordChange} />;
};

// Redirects away from /manager/licence when the MANAGER_LICENCE feature flag is disabled
export const ManagerLicenceRoute: React.FC = () => {
  const isManagerLicenceEnabled = useSelector((state: RootState) =>
    selectFeatureFlag(state, "MANAGER_LICENCE")
  );

  if (!isManagerLicenceEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LicencePage />;
};

// Blocks direct URL access to licence-gated manager pages (MED-1201): reports
// and facility settings require an active licence. Mirrors the sidebar lock —
// defense in depth so the route can't be reached even if the menu item is bypassed.
const RequireLicence: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const hasLicence = useHasLicence();

  if (!hasLicence) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// MED-1653 (MED-1411 scenario 12, case 4): a manager or secretary without a facility only sees
// the "no facility assigned" screen; pages that need a facility (staff, appointments, patients,
// licence...) were reachable by typing their address. They send back to /dashboard, which shows
// that screen. Profile, user settings, help and notifications stay available.
const RequireFacility: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const facilityUser = useFacilityUser();

  if (!facilityUser?.healthcareFacility?.id) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Scroll to top component
const ScrollToTop: React.FC = () => {
  useScrollToTop("auto");
  return null;
};

// Component to reset loader on app initialization
const AppInitializer: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Reset loader on app initialization to clear any stuck state
    dispatch(resetLoader());
  }, [dispatch]);

  return null;
};

function App() {
  return (
    <Provider store={store}>
      <ErrorBoundary>
        <AppInitializer />
        <GlobalLoader />
        <ErrorModal />
        <Toaster richColors position="top-right" />
        <Router>
          <AnalyticsPageViews />
          <ScrollToTop />
          <SessionProvider>
            <PinLockProvider>
            <PasswordChangeRequiredWrapper />
            <Routes>
              {/* Public routes */}
              <Route path="/set-password" element={<SetPassword />} />

              {/* Payment callback — outside DashboardLayout (full-screen) */}
              <Route
                path="/payment/callback"
                element={
                  <PrivateRoute allowedRoles={["manager"]}>
                    <PaymentCallbackPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/payment/cancelled"
                element={
                  <PrivateRoute allowedRoles={["manager"]}>
                    <PaymentCancelledPage />
                  </PrivateRoute>
                }
              />

              {/* Protected route outside DashboardLayout */}
              <Route
                path="/change-password"
                element={
                  <PrivateRoute allowedRoles={["manager", "secretary"]}>
                    <ChangePassword />
                  </PrivateRoute>
                }
              />

              <Route element={<DashboardLayout />}>
                {/* Root redirect to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" />} />

                {/* Main dashboard */}
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <DashboardRouter />
                    </PrivateRoute>
                  }
                />

                {/* Shared routes (manager and secretary) */}
                <Route
                  path="/appointments"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <RequireFacility>
                        <ManageAppointments />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/appointments/:appointmentId"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <RequireFacility>
                        <AppointmentDetails />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/patients"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <RequireFacility>
                        <ManagePatients />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/patients/:patientId"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <RequireFacility>
                        <PatientDetails />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <UserProfile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/user-settings"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <UserSettings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/help-support"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <HelpSupport />
                    </PrivateRoute>
                  }
                />

                {/* Manager only routes */}
                <Route
                  path="/manager/staff"
                  element={
                    <PrivateRoute allowedRoles={["manager"]}>
                      <RequireFacility>
                        <ManageStaff />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/manager/staff/:role/:staffId"
                  element={
                    <PrivateRoute allowedRoles={["manager"]}>
                      <RequireFacility>
                        <StaffDetails />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/manager/reports"
                  element={
                    <PrivateRoute allowedRoles={["manager"]}>
                      <RequireFacility>
                        <RequireLicence>
                          <ViewReports />
                        </RequireLicence>
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/manager/settings"
                  element={
                    <PrivateRoute allowedRoles={["manager"]}>
                      <RequireFacility>
                        <RequireLicence>
                          <FacilitySettings />
                        </RequireLicence>
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/manager/emergency"
                  element={
                    <PrivateRoute allowedRoles={["manager"]}>
                      <RequireFacility>
                        <EmergencyManagement />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/manager/licence"
                  element={
                    <PrivateRoute allowedRoles={["manager"]}>
                      <RequireFacility>
                        <ManagerLicenceRoute />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/manager/notifications"
                  element={
                    <PrivateRoute allowedRoles={["manager", "secretary"]}>
                      <Notifications />
                    </PrivateRoute>
                  }
                />

                {/* Secretary only routes */}
                <Route
                  path="/secretary/availability"
                  element={
                    <PrivateRoute allowedRoles={["secretary"]}>
                      <RequireFacility>
                        <ManageAvailability />
                      </RequireFacility>
                    </PrivateRoute>
                  }
                />
              </Route>

              {/* 404 Not Found - catch all undefined routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </PinLockProvider>
          </SessionProvider>
        </Router>
      </ErrorBoundary>
    </Provider>
  );
}

export default App;
